import { useState } from 'react';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    Alert,
    Snackbar,
    Card,
    CardContent,
    IconButton,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider
} from '@mui/material';
import * as Yup from 'yup';
import { Formik } from 'formik';
import MainCard from 'ui-component/cards/MainCard';
import Loading from 'components/Loading/Loading';
import { uploadPendencia } from 'services/pendencia';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import { gridSpacing } from 'store/constant';

const Pendencia = ({ ...others }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [unidadesNaoEncontradas, setUnidadesNaoEncontradas] = useState([]);
    const [detalhesUnidades, setDetalhesUnidades] = useState([]);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // Janeiro = 1

    const meses = [
        { value: 1, label: 'Janeiro' },
        { value: 2, label: 'Fevereiro' },
        { value: 3, label: 'Março' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Maio' },
        { value: 6, label: 'Junho' },
        { value: 7, label: 'Julho' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Setembro' },
        { value: 10, label: 'Outubro' },
        { value: 11, label: 'Novembro' },
        { value: 12, label: 'Dezembro' }
    ];

    const themeButton = createTheme({
        status: {
            danger: '#e53e3e'
        },
        palette: {
            primary: {
                main: '#0971f1',
                darker: '#053e85'
            }
        }
    });

    const validationSchema = Yup.object().shape({
        mes: Yup.number()
            .required('Mês é obrigatório')
            .min(1, 'Mês inválido')
            .max(12, 'Mês inválido'),
        ano: Yup.number()
            .required('Ano é obrigatório')
            .min(2000, 'Ano deve ser maior que 2000')
            .max(2100, 'Ano deve ser menor que 2100')
            .integer('Ano deve ser um número inteiro'),
        file: Yup.mixed()
            .required('Arquivo é obrigatório')
            .test('fileType', 'Apenas arquivos Excel são permitidos (.xlsx, .xls)', (value) => {
                if (!value) return false;
                const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
                const validExtensions = ['xlsx', 'xls'];
                const fileName = value.name || '';
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                return validTypes.includes(value.type) || validExtensions.includes(fileExtension);
            })
    });

    const handleFileChange = (event, setFieldValue) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFieldValue('file', file);
        }
    };

    const handleRemoveFile = (setFieldValue) => {
        setSelectedFile(null);
        setFieldValue('file', null);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            if (!selectedFile) {
                setError('Por favor, selecione um arquivo.');
                setLoading(false);
                return;
            }

            const response = await uploadPendencia(selectedFile, values.mes, values.ano);

            if (response.data.success) {
                setSuccess(response.data.success);
                setUnidadesNaoEncontradas([]);
                setDetalhesUnidades([]);
                setSelectedFile(null);
                resetForm();
                setTimeout(() => {
                    setSuccess('');
                }, 5000);
            } else {
                setError(response.data.error || 'Erro ao fazer upload do arquivo.');
            }
        } catch (err) {
            const errorData = err.response?.data || {};
            const errorMessage = errorData.error || 'Erro ao fazer upload do arquivo. Tente novamente.';
            setError(errorMessage);
            
            // Se houver unidades não encontradas, exibir detalhes
            if (errorData.unidades_nao_encontradas && errorData.unidades_nao_encontradas.length > 0) {
                setUnidadesNaoEncontradas(errorData.unidades_nao_encontradas);
                setDetalhesUnidades(errorData.detalhes_unidades || []);
            } else {
                setUnidadesNaoEncontradas([]);
                setDetalhesUnidades([]);
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={themeButton}>
            <MainCard title="Upload de Pendências">
                <Formik
                    initialValues={{
                        mes: currentMonth,
                        ano: currentYear,
                        file: null
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ errors, touched, handleSubmit, setFieldValue, values }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Instruções:</strong>
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                                                <li>A planilha deve ter as seguintes colunas: Unidade (A), Equipamentos (B), Série/Patrimônio (C)</li>
                                                <li>As pendências são carregadas uma vez por mês e podem conter todas as unidades misturadas</li>
                                                <li>Ao fazer upload de uma nova planilha para o mesmo mês/ano, os dados anteriores serão substituídos</li>
                                                <li>O sistema tentará encontrar a unidade automaticamente mesmo com pequenas diferenças no nome</li>
                                            </ul>
                                        </Typography>
                                    </Alert>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Mês"
                                        name="mes"
                                        value={values.mes}
                                        onChange={(e) => setFieldValue('mes', parseInt(e.target.value))}
                                        error={touched.mes && Boolean(errors.mes)}
                                        helperText={touched.mes && errors.mes}
                                    >
                                        {meses.map((mes) => (
                                            <MenuItem key={mes.value} value={mes.value}>
                                                {mes.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Ano"
                                        name="ano"
                                        type="number"
                                        value={values.ano}
                                        onChange={(e) => setFieldValue('ano', parseInt(e.target.value) || '')}
                                        error={touched.ano && Boolean(errors.ano)}
                                        helperText={touched.ano && errors.ano}
                                        InputProps={{
                                            inputProps: { min: 2000, max: 2100 }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <input
                                            accept=".xlsx,.xls"
                                            style={{ display: 'none' }}
                                            id="file-upload-pendencia"
                                            type="file"
                                            onChange={(e) => handleFileChange(e, setFieldValue)}
                                        />
                                        <label htmlFor="file-upload-pendencia">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                fullWidth
                                                startIcon={<CloudUploadIcon />}
                                                disabled={loading}
                                            >
                                                Selecionar Arquivo Excel
                                            </Button>
                                        </label>
                                        {touched.file && errors.file && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                {errors.file}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {selectedFile && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <DescriptionIcon color="primary" />
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {selectedFile.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(selectedFile.size / 1024).toFixed(2)} KB
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveFile(setFieldValue)}
                                                        disabled={loading}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box display="flex" gap={2} justifyContent="flex-end">
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading || !selectedFile}
                                            startIcon={loading ? <Loading /> : <CloudUploadIcon />}
                                        >
                                            {loading ? 'Enviando...' : 'Enviar Pendências'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>

                <Snackbar
                    open={!!success}
                    autoHideDuration={6000}
                    onClose={() => setSuccess('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!error}
                    autoHideDuration={unidadesNaoEncontradas.length > 0 ? 0 : 6000}
                    onClose={() => {
                        setError('');
                        setUnidadesNaoEncontradas([]);
                        setDetalhesUnidades([]);
                    }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert 
                        onClose={() => {
                            setError('');
                            setUnidadesNaoEncontradas([]);
                            setDetalhesUnidades([]);
                        }} 
                        severity="error" 
                        sx={{ width: '100%', maxWidth: '600px' }}
                    >
                        <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                            {error}
                        </Typography>
                        {unidadesNaoEncontradas.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                    Unidades não encontradas ({unidadesNaoEncontradas.length}):
                                </Typography>
                                <List dense sx={{ bgcolor: 'rgba(211, 47, 47, 0.08)', borderRadius: 1, p: 1 }}>
                                    {unidadesNaoEncontradas.map((unidade, index) => {
                                        const detalhe = detalhesUnidades.find(d => d.nome === unidade);
                                        return (
                                            <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {unidade}
                                                            </Typography>
                                                            {detalhe && (
                                                                <Chip 
                                                                    label={`${detalhe.total_linhas} linha(s)`} 
                                                                    size="small" 
                                                                    color="error"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        detalhe && detalhe.linhas && detalhe.linhas.length > 0 && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Linhas: {detalhe.linhas.join(', ')}
                                                                {detalhe.total_linhas > 3 && ` e mais ${detalhe.total_linhas - 3}`}
                                                            </Typography>
                                                        )
                                                    }
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Por favor, corrija o nome das unidades na planilha para corresponder aos nomes cadastrados no sistema e tente novamente.
                                </Typography>
                            </Box>
                        )}
                    </Alert>
                </Snackbar>
            </MainCard>
        </ThemeProvider>
    );
};

export default Pendencia;

